declare global {
	interface Window {
		// Provided by Pagefind UI bundle (loaded dynamically)
		PagefindUI?: new (opts: {
			element: HTMLElement | string;
			bundlePath?: string;
			resetStyles?: boolean;
			showImages?: boolean;
			showSubResults?: boolean;
			debounceTimeoutMs?: number;
			// allow extra Pagefind options to pass through
			[key: string]: unknown;
		}) => { triggerSearch: (term: string) => void; destroy: () => void };

		// Resolves to the working base path (e.g. "/pagefind/" or "/public/pagefind/")
		__pagefindUiLoader?: Promise<string>;
	}
}

type PagefindUiInstance = {
	triggerSearch: (term: string) => void;
	destroy: () => void;
};

type InitPagefindSearchOptions = {
	/**
	 * Debounce the user's typing before triggering search.
	 * Default: 300ms
	 */
	inputDebounceMs?: number;

	/**
	 * Debounce inside PagefindUI itself (its own internal input).
	 * Default: 300ms
	 *
	 * Note: since we call `triggerSearch()` programmatically, this is less important,
	 * but leaving it on keeps behavior predictable if the internal form ever shows.
	 */
	pagefindUiDebounceMs?: number;
};

function getPagefindBaseCandidates(): string[] {
	const candidates = ["/pagefind/", "/public/pagefind/"];
	// Dev server seems to serve assets under /public/* (based on service worker path logic)
	if (window.location.host.includes(":")) {
		return ["/public/pagefind/", "/pagefind/"];
	}
	return candidates;
}

function safeIdSuffix(basePath: string): string {
	return basePath.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function detectPagefindUiBasePathFromDom(): string | null {
	// If PagefindUI is already loaded, infer which basePath it came from so we load
	// pagefind-entry.json/wasm from the same place.
	const script = Array.from(document.querySelectorAll("script[src]")).find(
		(s) => (s as HTMLScriptElement).src.includes("pagefind-ui.js"),
	) as HTMLScriptElement | undefined;

	const link = Array.from(
		document.querySelectorAll('link[rel="stylesheet"][href]'),
	).find((l) => (l as HTMLLinkElement).href.includes("pagefind-ui.css")) as
		| HTMLLinkElement
		| undefined;

	const tryUrl = (raw: string): string | null => {
		try {
			const url = new URL(raw, window.location.href);
			const pathname = url.pathname;
			const marker = "pagefind-ui.";
			const idx = pathname.lastIndexOf(marker);
			if (idx === -1) return null;
			const base = pathname.slice(0, idx);
			return base.endsWith("/") ? base : `${base}/`;
		} catch {
			return null;
		}
	};

	return tryUrl(script?.src ?? "") ?? tryUrl(link?.href ?? "") ?? null;
}

async function ensurePagefindUiLoaded(): Promise<string> {
	// If already loaded, infer base path from DOM (important on dev where it might be /public/pagefind/)
	if (window.PagefindUI) {
		const inferred = detectPagefindUiBasePathFromDom();
		const basePath = inferred ?? "/pagefind/";
		console.log("[pagefind] ensurePagefindUiLoaded: already available", {
			basePath,
		});
		return basePath;
	}

	if (window.__pagefindUiLoader) {
		console.log("[pagefind] ensurePagefindUiLoaded: using existing loader");
		return window.__pagefindUiLoader;
	}

	window.__pagefindUiLoader = (async () => {
		const candidates = getPagefindBaseCandidates();

		for (const basePath of candidates) {
			const suffix = safeIdSuffix(basePath);
			const cssId = `pagefind-ui-css-${suffix}`;
			const jsId = `pagefind-ui-js-${suffix}`;

			console.log("[pagefind] ensurePagefindUiLoaded: trying", {
				basePath,
			});

			try {
				loadCssOnce(`${basePath}pagefind-ui.css`, cssId);
				await loadScriptOnce(`${basePath}pagefind-ui.js`, jsId);

				if (!window.PagefindUI) {
					throw new Error("PagefindUI global not found after script load");
				}

				console.log("[pagefind] ensurePagefindUiLoaded: PagefindUI ready", {
					basePath,
				});
				return basePath;
			} catch (err) {
				console.warn(
					"[pagefind] ensurePagefindUiLoaded: failed for basePath",
					basePath,
					err,
				);
				// cleanup failed attempt so fallback can try cleanly
				removeElementById(jsId);
				// leave CSS link in place; it won't break anything even if 404'd
			}
		}

		throw new Error(
			"Unable to load Pagefind UI from any known base path. Tried: " +
				candidates.join(", "),
		);
	})();

	return window.__pagefindUiLoader;
}

function loadCssOnce(href: string, id: string) {
	if (document.getElementById(id)) {
		console.log("[pagefind] loadCssOnce: already present", { id, href });
		return;
	}
	console.log("[pagefind] loadCssOnce: adding", { id, href });
	const link = document.createElement("link");
	link.id = id;
	link.rel = "stylesheet";
	link.href = href;
	// Not all browsers fire onerror for CSS reliably, but this helps when it does.
	link.onerror = () => {
		console.warn("[pagefind] loadCssOnce: failed to load CSS", { id, href });
	};
	document.head.appendChild(link);
}

function loadScriptOnce(src: string, id: string): Promise<void> {
	const existing = document.getElementById(id) as HTMLScriptElement | null;
	if (existing) {
		console.log("[pagefind] loadScriptOnce: script already exists", {
			id,
			src,
		});
		// If it's already loaded, resolve immediately; otherwise wait for load/error.
		if ((existing as any).dataset?.loaded === "true") return Promise.resolve();
		return new Promise((resolve, reject) => {
			existing.addEventListener("load", () => resolve(), { once: true });
			existing.addEventListener(
				"error",
				() => reject(new Error(`Failed to load ${src}`)),
				{ once: true },
			);
		});
	}

	console.log("[pagefind] loadScriptOnce: adding", { id, src });
	return new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.id = id;
		script.src = src;
		script.async = true;
		script.onload = () => {
			(script as any).dataset.loaded = "true";
			console.log("[pagefind] loadScriptOnce: loaded", { id, src });
			resolve();
		};
		script.onerror = () => reject(new Error(`Failed to load ${src}`));
		document.head.appendChild(script);
	});
}

function removeElementById(id: string) {
	const el = document.getElementById(id);
	if (!el) return;
	el.remove();
}

/**
 * Wires the existing nav search input to Pagefind.
 *
 * Requirements:
 * - The site must serve Pagefind assets under /pagefind/ OR /public/pagefind/ (we auto-detect).
 *
 * Behavior:
 * - Intercepts the nav search form submit so it does NOT navigate to Google.
 * - When user types, shows Pagefind results in the nav's `.scrollable` area and hides the nav link lists.
 */
export function initPagefindSearch(
	navRoot: HTMLElement,
	opts: InitPagefindSearchOptions = {},
): void {
	const inputDebounceMs = opts.inputDebounceMs ?? 300;
	const pagefindUiDebounceMs = opts.pagefindUiDebounceMs ?? 300;

	const searchBox =
		navRoot.querySelector("#Search") ?? navRoot.querySelector("#search");
	if (!searchBox) {
		console.log("[pagefind] initPagefindSearch: no #Search element found");
		return;
	}

	const form =
		(searchBox.querySelector("form") as HTMLFormElement | null) ??
		(navRoot.querySelector(
			'form[method="get"][action="https://www.google.com/search"]',
		) as HTMLFormElement | null);
	if (!form) {
		console.log("[pagefind] initPagefindSearch: no form found under #Search");
		return;
	}

	const input = form.querySelector(
		'input[name="q"][type="text"]',
	) as HTMLInputElement | null;
	if (!input) {
		console.log(
			"[pagefind] initPagefindSearch: no input[name=q][type=text] found",
		);
		return;
	}

	const scrollable =
		(navRoot.querySelector(".scrollable") as HTMLElement | null) ?? navRoot;

	// prevent double-init if resize fires multiple times
	if (scrollable.querySelector('[data-pagefind-host="true"]')) {
		console.log("[pagefind] initPagefindSearch: already initialised");
		return;
	}

	console.log("[pagefind] initPagefindSearch: wiring input", {
		formAction: form.getAttribute("action"),
		inputName: input.name,
		inputType: input.type,
		hasScrollable: Boolean(navRoot.querySelector(".scrollable")),
		baseCandidates: getPagefindBaseCandidates(),
		inputDebounceMs,
		pagefindUiDebounceMs,
	});

	const host = document.createElement("div");
	host.dataset.pagefindHost = "true";
	host.style.display = "none";
	scrollable.prepend(host);

	// Make sure there is *some* visible feedback if Pagefind fails to load.
	const showHostMessage = (message: string) => {
		host.style.display = "block";
		host.innerHTML = "";
		const msg = document.createElement("div");
		msg.style.padding = "0.75em";
		msg.style.border = "1px solid var(--border)";
		msg.style.background = "var(--surface)";
		msg.style.color = "var(--text)";
		msg.textContent = message;
		host.appendChild(msg);
	};

	let pagefindUi: PagefindUiInstance | null = null;

	const ensureUi = async (): Promise<PagefindUiInstance> => {
		if (pagefindUi) return pagefindUi;

		console.log("[pagefind] ensureUi: loading Pagefind UI assets...");
		showHostMessage("Loading search…");

		const basePath = await ensurePagefindUiLoaded();

		// Verify the index exists. If this is wrong, Pagefind UI often looks like it's "stuck loading".
		const entryUrl = `${basePath}pagefind-entry.json`;
		try {
			const res = await fetch(entryUrl, { cache: "no-store" });
			console.log("[pagefind] ensureUi: pagefind-entry.json", {
				entryUrl,
				ok: res.ok,
				status: res.status,
			});
			if (!res.ok) {
				throw new Error(`Missing Pagefind index: ${entryUrl} (${res.status})`);
			}
		} catch (err) {
			console.error(
				"[pagefind] ensureUi: failed to fetch pagefind-entry.json",
				err,
			);
			showHostMessage(
				`Search index not found at ${entryUrl}. (This usually means the basePath is wrong or Pagefind build output isn't being served.)`,
			);
			throw err;
		}

		if (!window.PagefindUI) {
			console.error(
				"[pagefind] ensureUi: PagefindUI global missing after load",
			);
			throw new Error("PagefindUI did not load");
		}

		// Remove the "Loading search…" message so the UI can render cleanly.
		host.innerHTML = "";
		host.style.display = "block";

		console.log("[pagefind] ensureUi: creating PagefindUI instance", {
			basePath,
		});
		pagefindUi = new window.PagefindUI({
			element: host,
			bundlePath: basePath,
			resetStyles: false,
			showImages: false,
			showSubResults: false,
			debounceTimeoutMs: pagefindUiDebounceMs,
		});

		// hide PagefindUI's own built-in search input; we use the existing nav input.
		const hideInternal = () => {
			const internalForm = host.querySelector(
				".pagefind-ui__form",
			) as HTMLElement | null;
			if (internalForm) {
				internalForm.style.display = "none";
				console.log("[pagefind] ensureUi: hid internal PagefindUI form");
			}
		};
		hideInternal();
		setTimeout(hideInternal, 0);

		return pagefindUi;
	};

	const setNavLinksVisible = (visible: boolean) => {
		const linkSections = Array.from(
			scrollable.querySelectorAll(".relatedLinks"),
		) as HTMLElement[];
		linkSections.forEach((el) => {
			el.style.display = visible ? "" : "none";
		});
		console.log("[pagefind] nav link sections", {
			visible,
			sections: linkSections.length,
		});
	};

	let lastTerm = "";
	let inputDebounceTimer: number | undefined;

	const runSearch = async (source: "input" | "submit") => {
		const term = input.value.trim();
		lastTerm = term;

		console.log("[pagefind] runSearch", {
			source,
			term,
			termLength: term.length,
		});

		if (!term) {
			host.style.display = "none";
			setNavLinksVisible(true);
			return;
		}

		try {
			host.style.display = "block";
			setNavLinksVisible(false);

			const ui = await ensureUi();

			// If the user changed while we were loading assets
			if (lastTerm !== term) {
				console.log("[pagefind] runSearch: term changed while loading", {
					was: term,
					now: lastTerm,
				});
				return;
			}

			console.log("[pagefind] runSearch: triggerSearch()", term);
			ui.triggerSearch(term);
		} catch (err) {
			console.error("[pagefind] search failed", err);
			showHostMessage(
				"Search failed to load. Check DevTools console for [pagefind] errors.",
			);
		}
	};

	// Intercept submit so it doesn't navigate to Google
	if (form.dataset.pagefindSubmitBound !== "true") {
		form.dataset.pagefindSubmitBound = "true";
		form.addEventListener("submit", (e) => {
			console.log("[pagefind] form submit intercepted (Go clicked / Enter)");
			e.preventDefault();
			e.stopPropagation();

			if (inputDebounceTimer != null) {
				window.clearTimeout(inputDebounceTimer);
				inputDebounceTimer = undefined;
			}

			void runSearch("submit");
		});
	} else {
		console.log("[pagefind] submit handler already bound");
	}

	if (input.dataset.pagefindInputBound !== "true") {
		input.dataset.pagefindInputBound = "true";

		input.addEventListener("input", () => {
			// This happens on keypress/typing/paste etc.
			console.log("[pagefind] input event", { value: input.value });

			if (inputDebounceTimer != null) {
				window.clearTimeout(inputDebounceTimer);
			}

			inputDebounceTimer = window.setTimeout(() => {
				void runSearch("input");
			}, inputDebounceMs);
		});

		input.addEventListener("keydown", (e) => {
			if (e.key === "Escape") {
				console.log("[pagefind] Escape: clearing search");

				if (inputDebounceTimer != null) {
					window.clearTimeout(inputDebounceTimer);
					inputDebounceTimer = undefined;
				}

				input.value = "";
				host.style.display = "none";
				setNavLinksVisible(true);
			}
		});

		// Warm-load Pagefind when the user focuses the input (optional)
		input.addEventListener("focus", () => {
			console.log("[pagefind] input focus: warm-loading assets");
			void ensurePagefindUiLoaded().catch((err) => {
				console.warn("[pagefind] warm-load failed", err);
			});
		});
	} else {
		console.log("[pagefind] input handlers already bound");
	}
}
